import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getUserInfo } from '../../lib/api/Auth-Service'
import {
  destroyStarshipLab,
  launchStarshipLab,
  resetLoadBalancerCounter,
} from '../../lib/api/Lab-Service'
import LoadingAnimationForLab from '../common/Loader/LoadingAnimationForLab'
import NoAccessSection from '../common/NoAccessSection'
import { useLabWebSocket } from '../../hooks/Lab/useLabWebSocket'
import { addSpecificLabCount } from '../../lib/api/LabInfo-Service'
import {
  addSubscriptionSpecificLabCount,
  resetSubscriptionLoadBalancerCounter,
} from '../../lib/api/Subscription-Service'

// Custom hook to manage lab configuration data
const useLabData = () => {
  // Retrieve lab data from localStorage
  const allLabData = JSON.parse(localStorage.getItem('startingLabData')) || null
  const isLabDataNull = allLabData === null
  const { labGroupID, labId, duration, configId, counterID, moduleID } = allLabData || {}
  // Check if a lab is currently running
  const isLabRunning = Boolean(localStorage.getItem('runningLabData'))

  return {
    allLabData,
    isLabDataNull,
    labGroupID,
    labId,
    duration,
    configId,
    isLabRunning,
    counterID,
    moduleID,
  }
}

// Custom hook to manage local lab data
const useLocalLabData = (labId, duration) => {
  const localLabDataRef = useRef({})

  // Function to update local lab data
  const updateLocalLabData = useCallback((newData) => {
    localLabDataRef.current = { ...localLabDataRef.current, ...newData }
  }, [])

  // Function to update task information
  const updateTask = useCallback(
    (newTaskId) => {
      updateLocalLabData({
        labId,
        task_id: newTaskId,
        validation: duration * 60 * 1000 + Date.now(),
      })
    },
    [labId, duration, updateLocalLabData]
  )

  return { localLabDataRef, updateLocalLabData, updateTask }
}

const LabLoader = () => {
  const navigate = useNavigate()
  const labStartedRef = useRef(false)
  const [taskId, setTaskId] = useState(null)
  const { plan } = useParams()
  const isRegularPlan = plan === 'regular'
  // Fetch user information
  const { data: userinfo } = useQuery(['userinfo'], getUserInfo)
  // Get lab configuration data
  const {
    isLabDataNull,
    labGroupID,
    labId,
    duration,
    configId,
    isLabRunning,
    counterID,
    moduleID,
  } = useLabData()
  // Set up local lab data management
  const { localLabDataRef, updateLocalLabData, updateTask } = useLocalLabData(labId, duration)
  // Set up WebSocket connection
  const { lastJsonMessage } = useLabWebSocket(taskId)

  const userID = userinfo?.data?.uid

  const isDeletingTask = Boolean(localStorage.getItem('deletingTask'))

  // If no lab data is found, show NoAccessSection
  if (isLabDataNull) {
    return <NoAccessSection />
  }

  // Function to save lab data and navigate to playground
  const saveLabDataAndNavigate = useCallback(async () => {
    sessionStorage.setItem('reloaded', 'false')
    localStorage.setItem('runningLabData', JSON.stringify(localLabDataRef.current))
    localStorage.removeItem('task_id')

    localStorage.removeItem('awsFetchConsole')
    localStorage.removeItem('loadBalancers')
    localStorage.removeItem('parrotData')
    localStorage.removeItem('vscodeData')

    if (isRegularPlan) {
      try {
        await resetLoadBalancerCounter(userID, counterID)
      } catch (err) {
        //error code
      }
      return navigate(`/launch/playground/regular/${labGroupID}/${moduleID}/${labId}`)
    }
    try {
      await resetSubscriptionLoadBalancerCounter(userID, counterID)
    } catch (err) {
      //error code
    }

    return navigate(`/launch/playground/subscription/${labGroupID}/${moduleID}/${labId}`)
  }, [labGroupID, labId, navigate, localLabDataRef])

  // Function to handle lab destruction process
  const handleLabDestruction = useCallback(
    async (taskIdFromError) => {
      try {
        // Attempt to destroy the lab
        const destroyResponse = await destroyStarshipLab({ task_id: taskIdFromError })
        if (destroyResponse.status === 201) {
          // Lab destruction initiated successfully
          localStorage.setItem('task_id', taskIdFromError)
          localStorage.setItem('deletingTask', 1)
          updateTask(taskIdFromError)
          setTaskId(taskIdFromError)
          return true
        }
        if (destroyResponse.status === 200) {
          // Lab destroyed successfully
          localStorage.removeItem('task_id')
          localStorage.removeItem('deletingTask')
          handleStartLab()
          return true
        }
        // if (destroyResponse.task_state === 400) {
        //   // Lab destroyed successfully
        //   localStorage.removeItem('task_id')
        //   localStorage.removeItem('deletingTask')
        //   handleStartLab()
        //   return true
        // }
      } catch (destroyError) {
        console.error('Error destroying lab:', destroyError)
        alert(
          `Error destroying lab: ${
            destroyError.response?.data?.error || 'Contact Engineering Team'
          }`
        )

        return navigate(-1)
      }
      return false
    },
    [updateTask]
  )

  // Main function to start the lab
  const handleStartLab = useCallback(async () => {
    const isDeletingTask = Boolean(localStorage.getItem('deletingTask'))

    if (isDeletingTask) return
    // If lab is already running, navigate to lab group details
    if (isLabRunning) {
      if (isRegularPlan) {
        return navigate(`/lab-group-modules/${labGroupID}`)
      }
      return navigate(`/subscription`)
    }
    // Prevent multiple starts and ensure necessary data is available
    if (labStartedRef.current || !userID || !configId) return
    labStartedRef.current = true

    const isTaskStored = Boolean(localStorage.getItem('task_id'))

    if (isTaskStored) {
      // If task is already stored, update with stored task ID
      const storedTaskId = localStorage.getItem('task_id')
      updateTask(storedTaskId)
      setTaskId(storedTaskId)
    } else {
      try {
        // Attempt to launch the lab
        const response = await launchStarshipLab({ user_id: userID, configuration_id: configId })
        if (response.status === 201) {
          const newTaskId = response.data?.task_id
          localStorage.setItem('task_id', newTaskId)
          updateTask(newTaskId)
          setTaskId(newTaskId)
          if (isRegularPlan) await addSpecificLabCount(counterID)
          else await addSubscriptionSpecificLabCount(counterID)
        }
      } catch (error) {
        const taskIdFromError = error.response?.data?.task_id
        if (taskIdFromError) {
          // If we got a task ID from the error, attempt to destroy the lab
          setTaskId(taskIdFromError)
          await handleLabDestruction(taskIdFromError)
        } else {
          alert(`Error launching lab: ${error.response?.data?.error || 'Contact Engineering Team'}`)
          navigate(-1)
        }
      }
    }
  }, [
    userID,
    configId,
    labGroupID,
    isLabRunning,
    navigate,
    updateTask,
    handleLabDestruction,
    labId,
  ])

  // Start the lab when component mounts
  useEffect(() => {
    handleStartLab()
  }, [handleStartLab])

  // Handle WebSocket messages and update local data
  useEffect(() => {
    if (isDeletingTask) {
      if (lastJsonMessage?.task_state === 'deleted') {
        // Lab has been successfully deleted
        localStorage.removeItem('task_id')
        localStorage.removeItem('deletingTask')
        labStartedRef.current = false
        // Restart the lab launch process
        handleStartLab()
      }
    } else if (lastJsonMessage?.task_state === 'created') {
      // Lab has been created, update terminal URL
      const { node_type, bm_id, response } = lastJsonMessage
      const terminalURL = `https://term-${bm_id.split('-')[1]}.poridhi.io/ssh/${node_type}/${
        response.master_node.vm_name
      }`

      updateLocalLabData({ term_url: terminalURL, bm_id: bm_id })
    } else if (lastJsonMessage?.task_state === '404') {
      // Lab has been successfully deleted
      localStorage.removeItem('task_id')
      localStorage.removeItem('deletingTask')
      labStartedRef.current = false
      // Restart the lab launch process
      handleStartLab()
    }
  }, [lastJsonMessage, updateLocalLabData, handleStartLab, isDeletingTask])

  // Navigate to playground when all necessary data is available
  useEffect(() => {
    const { task_id, term_url, validation } = localLabDataRef.current
    if (task_id && term_url && validation) {
      saveLabDataAndNavigate()
    }
  }, [lastJsonMessage, saveLabDataAndNavigate])

  // Render loading animation while lab is being prepared
  return <LoadingAnimationForLab />
}

export default LabLoader
